import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { getSessionUserId } from '../../../../../utils/SessionUser';
import AlertDialog from '../../Shared/AlertDialog';

export default function PeticionFechaModal({ open, cursoInfo, onClose, onSuccess }) {
    const [peticiones, setPeticiones] = useState([]);
    const [loading, setLoading] = useState(false);

    // Modal de alertas estándar LMS
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });

    // Form state
    const [selectedPeticion, setSelectedPeticion] = useState(null);
    const [nuevaFecha, setNuevaFecha] = useState('');
    const [statusDecision, setStatusDecision] = useState(''); // 'approve' | 'reject' | ''

    const [cursoDuracion, setCursoDuracion] = useState(0);
    const [diasFestivos, setDiasFestivos] = useState([]);

    useEffect(() => {
        if (open && cursoInfo?.idEstudianteCurso) {
            loadPeticiones(cursoInfo.idEstudianteCurso);
        }
    }, [open, cursoInfo]);

    const loadPeticiones = async (idEstudianteCurso) => {
        setLoading(true);
        try {
            // Extract api base from an existing endpoint
            const apiBase = ENDPOINTS.cursos ? ENDPOINTS.cursos.replace('/Curso', '') : 'http://localhost:5248/api';

            let url = ENDPOINTS.peticionFechaExtendida
                ? `${ENDPOINTS.peticionFechaExtendida}/EstudianteCurso/${idEstudianteCurso}`
                : `${apiBase}/PeticionFechaExtendida/EstudianteCurso/${idEstudianteCurso}`;

            const urlDias = `${apiBase}/DiasFestivosInhabiles`;
            const urlCurso = `${apiBase}/Curso/${cursoInfo.idCurso}`;

            const [resPeticiones, resDias, resCurso] = await Promise.all([
                axios.get(url),
                axios.get(urlDias).catch(() => ({ data: [] })),
                axios.get(urlCurso).catch(() => ({ data: { duracion: 0 } }))
            ]);

            const data = resPeticiones.data || [];
            const pendientes = data.filter(p => (p.atendido ?? p.Atendido) !== true);
            const festivos = resDias.data || [];
            const duracion = resCurso.data?.duracion ?? resCurso.data?.Duracion ?? 0;

            setDiasFestivos(festivos);
            setCursoDuracion(duracion);

            setPeticiones(pendientes);
            if (pendientes.length > 0) {
                const latest = pendientes[0];
                setSelectedPeticion(latest);
            }
        } catch (e) {
            console.error("Error cargando petición:", e);
            setAlertDialog({
                open: true,
                title: 'Error de Carga',
                message: "No se pudo cargar la petición de extensión.",
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const calculateNewDate = (days, festivos = []) => {
        let daysToAdd = parseInt(days, 10) || 0;
        let currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        const isHoliday = (d) => {
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            const dStr = `${yyyy}-${mm}-${dd}`;

            return festivos.some(df => {
                const festiva = df.fechaFestiva || df.FechaFestiva;
                const recurrente = df.esRecurrente ?? df.EsRecurrente;

                if (!festiva) return false;
                const festivaDate = festiva.split('T')[0];
                if (recurrente) {
                    return festivaDate.substring(5) === dStr.substring(5);
                }
                return festivaDate === dStr;
            });
        };

        while (daysToAdd > 0) {
            currentDate.setDate(currentDate.getDate() + 1);
            const dayOfWeek = currentDate.getDay();

            if (dayOfWeek === 0 || dayOfWeek === 6) continue;
            if (isHoliday(currentDate)) continue;

            daysToAdd--;
        }

        const yyyy = currentDate.getFullYear();
        const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
        const dd = String(currentDate.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const handleSave = async () => {
        if (!statusDecision) {
            setAlertDialog({
                open: true,
                title: 'Validación',
                message: "Seleccione si aprueba o rechaza la petición.",
                variant: 'warning'
            });
            return;
        }

        if (statusDecision === 'approve' && !nuevaFecha) {
            setAlertDialog({
                open: true,
                title: 'Validación',
                message: "Seleccione una nueva fecha límite para aprobar la petición.",
                variant: 'warning'
            });
            return;
        }

        try {
            setLoading(true);
            const isApproved = statusDecision === 'approve';
            const idUsuario = getSessionUserId();

            const apiBase = ENDPOINTS.cursos ? ENDPOINTS.cursos.replace('/Curso', '') : 'http://localhost:5248/api';
            const urlPet = ENDPOINTS.peticionFechaExtendida || `${apiBase}/PeticionFechaExtendida`;

            // 1. Update petition
            const peticionActualizada = {
                ...selectedPeticion,
                atendido: true,
                Atendido: true,
                aceptado: isApproved,
                Aceptado: isApproved,
                idUsuarioAtendedor: idUsuario,
                IdUsuarioAtendedor: idUsuario,
                fechaActual: isApproved ? new Date(nuevaFecha).toISOString() : (selectedPeticion.fechaActual ?? selectedPeticion.FechaActual),
                FechaActual: isApproved ? new Date(nuevaFecha).toISOString() : (selectedPeticion.fechaActual ?? selectedPeticion.FechaActual)
            };

            await axios.put(urlPet, peticionActualizada);

            // 2. If approved, update EstudianteCurso
            if (isApproved) {
                const urlEstCur = ENDPOINTS.estudiantesCurso || `${apiBase}/EstudianteCurso`;
                const resEC = await axios.get(`${urlEstCur}/${cursoInfo.idEstudianteCurso}`);
                if (resEC.data) {
                    const ecUpdated = { 
                        ...resEC.data, 
                        fechaTermino: new Date(nuevaFecha).toISOString(),
                        FechaTermino: new Date(nuevaFecha).toISOString()
                    };
                    await axios.put(urlEstCur, ecUpdated);
                }
            }

            onSuccess("La petición se ha guardado correctamente.", "success", "Éxito");
        } catch (e) {
            console.error("Error actualizando la petición:", e);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: "Ocurrió un error al procesar la petición.",
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    const modalReady = !loading && selectedPeticion;
    const isAlreadyAttended = modalReady && selectedPeticion.atendido;

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 2000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(600px, 96vw)',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {/* Header */}
                <div
                    className="roles-topbar"
                    style={{
                        marginBottom: 0,
                        padding: '1rem 1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #e2e8f0'
                    }}
                >
                    <h3 className="roles-title" style={{ margin: 0 }}>
                        Gestionar Petición de Extensión
                    </h3>
                    <button className="roles-btn" onClick={onClose} aria-label="Cerrar" style={{ border: 'none', background: 'transparent', fontSize: '1.25rem', cursor: 'pointer' }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', flexGrow: 1 }}>
                    {loading && !selectedPeticion ? (
                        <p style={{ textAlign: 'center' }}>Cargando datos...</p>
                    ) : modalReady ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: 8 }}>
                                <p style={{ margin: '0 0 0.5rem 0' }}><strong>Curso:</strong> {cursoInfo.nombreCurso}</p>
                                <p style={{ margin: 0 }}><strong>Fecha Anterior:</strong> {selectedPeticion.fechaAnterior ? new Date(selectedPeticion.fechaAnterior).toLocaleDateString() : 'N/A'}</p>
                            </div>

                            {isAlreadyAttended ? (
                                <div style={{ background: selectedPeticion.aceptado ? '#dcfce7' : '#fee2e2', color: selectedPeticion.aceptado ? '#166534' : '#991b1b', padding: '1rem', borderRadius: 8, textAlign: 'center' }}>
                                    <h4 style={{ margin: '0 0 0.5rem 0' }}>Esta petición ya fue atendida</h4>
                                    <p style={{ margin: 0 }}>
                                        Estado: <strong>{selectedPeticion.aceptado ? 'Aprobada' : 'Rechazada'}</strong>
                                    </p>
                                    {selectedPeticion.aceptado && selectedPeticion.fechaActual && (
                                        <p style={{ margin: '0.5rem 0 0 0' }}>Nueva fecha otorgada: {new Date(selectedPeticion.fechaActual).toLocaleDateString()}</p>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <label className="roles-title" style={{ fontSize: '.95rem', display: 'block', marginBottom: '0.5rem' }}>Decisión</label>
                                        <div style={{ display: 'flex', gap: '1rem' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="decision"
                                                    value="approve"
                                                    checked={statusDecision === 'approve'}
                                                    onChange={() => {
                                                        setStatusDecision('approve');
                                                        setNuevaFecha(calculateNewDate(cursoDuracion, diasFestivos));
                                                    }}
                                                />
                                                Aprobar Extensión
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="decision"
                                                    value="reject"
                                                    checked={statusDecision === 'reject'}
                                                    onChange={() => {
                                                        setStatusDecision('reject');
                                                        setNuevaFecha('');
                                                    }}
                                                />
                                                Rechazar
                                            </label>
                                        </div>
                                    </div>

                                    {statusDecision === 'approve' && (
                                        <div>
                                            <label className="roles-title" style={{ fontSize: '.95rem', display: 'block', marginBottom: '0.5rem' }}>Seleccionar Nueva Fecha Límite</label>
                                            <input
                                                type="date"
                                                className="infoField"
                                                style={{ width: '100%' }}
                                                value={nuevaFecha}
                                                onChange={(e) => setNuevaFecha(e.target.value)}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ) : (
                        <p style={{ textAlign: 'center', color: '#64748b' }}>No se encontraron peticiones para este curso.</p>
                    )}
                </div>

                {/* Footer */}
                <div
                    style={{
                        padding: '1rem 1.5rem',
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '.6rem',
                        background: '#f8fafc'
                    }}
                >
                    <button className="btn-cancel" onClick={onClose}>Cerrar</button>
                    {!loading && selectedPeticion && !selectedPeticion.atendido && (
                        <button
                            className="btn-action"
                            onClick={handleSave}
                            disabled={!statusDecision}
                            style={{ opacity: !statusDecision ? 0.6 : 1 }}
                        >
                            Guardar Decisión
                        </button>
                    )}
                </div>
            </div>
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={alertDialog.onClose || (() => setAlertDialog(prev => ({ ...prev, open: false })))}
            />
        </div>
    );
}
